import appConfig from 'app.config'
import { Education } from 'constants/enums/educationTypes'
import { EmploymentSector } from 'constants/enums/employmentSectorTypes'
import { Industry } from 'constants/enums/industries'
import { JobTitle } from 'constants/enums/jobTitleTypes'
import { OccupationType } from 'constants/enums/occupationTypes'
import { PropertyType } from 'constants/enums/propertyTypes'
import { employedOccupationTypes } from 'constants/values/kycValues'
import { yearsAgo, yearsInFuture } from 'formBuilder/src'
import { TranslationFile } from 'i18n'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { useTranslation } from 'react-i18next'
import {
	checkIsMobile,
	enumArrayToString,
	FormatEnumToList,
} from 'utils/commonhelpers'
import { formatToStringLocale } from 'utils/dateAndTimeHelpers'

const isMobile: boolean = checkIsMobile()

export const ApplicationEmploymentForm = (data: IApplicationInformation) => {
	const { t } = useTranslation([TranslationFile.Forms])
	return {
		fieldSetName: '',
		formName: 'ApplicationEmploymentForm',
		description: t('forms:EmploymentDescription'),
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'education',
				type: 'dropdown',
				label: t('forms:Education'),
				options: FormatEnumToList(
					Education,
					PropertyType.Education,
					TranslationFile.Dropdown,
				),
				value:
					data.employment?.education === Education.Unspecified
						? null
						: data.employment?.education,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownEducation'),
			},
			{
				required: true,
				controlName: 'occupationType',
				type: 'dropdown',
				label: t('forms:Occupation'),
				options: FormatEnumToList(
					OccupationType,
					PropertyType.Occupation,
					TranslationFile.Dropdown,
				),
				value: data.employment?.occupationType,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownOccupationType'),
			},
			{
				required: false,
				controlName: 'employerName',
				type: 'text',
				label: t('forms:EmployerName'),
				value: data.employment?.employerName,
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'employedSince',
				type: 'text',
				label: t('forms:EmployedSince'),
				description: 'DD/MM/YYYY',
				value: formatToStringLocale(data.employment?.employedSince),
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^(\\b([0][1-9]|[12][0-9]|3[0-1]|[1-9])\\b)([/])(\\b([0][1-9]|[1][0-2]|[0-9]|[1-9])\\b)([/])(\\b([12]([9]|[0])[0-9][0-9])\\b)$',
						message: t('forms:ValidationMsgBirthdate'),
					},
				],
				visible: true,
				col: 12,
			},
			{
				required: true,
				controlName: 'employmentSector',
				type: 'dropdown',
				label: t('forms:EmploymentSector'),
				options: FormatEnumToList(
					EmploymentSector,
					PropertyType.EmploymentSector,
					TranslationFile.Dropdown,
				),
				value: data.employment?.employmentSector,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownEmploymentSector'),
			},
			{
				required: true,
				controlName: 'monthlyNetIncome',
				type: 'text',
				label: t('forms:Income'),
				value: data.employment?.monthlyNetIncome,
				description: t('forms:OtherIncomeDescription'),
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^[0-9]*$',
						message: t('forms:ValidationMsgValue'),
					},
				],
				visible: true,
				col: 12,
			},
			{
				required: false,
				controlName: 'otherIncome',
				type: 'text',
				label: t('forms:OtherIncome'),
				value: data.employment?.otherIncome,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^[0-9]*$',
						message: t('forms:ValidationMsgValue'),
					},
				],
				visible: true,
				col: 12,
			},
		],
	}
}

const employedOccupationTypesList = enumArrayToString(
	OccupationType,
	employedOccupationTypes,
)

export const ApplicationEmploymentFormDe = (data: IApplicationInformation) => {
	const { t } = useTranslation([TranslationFile.Forms])
	return {
		fieldSetName: '',
		formName: 'ApplicationEmploymentForm',
		description: t('forms:EmploymentDescription'),
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				required: true,
				controlName: 'occupationType',
				type: 'dropdown',
				label: t('forms:Occupation'),
				options: FormatEnumToList(
					OccupationType,
					PropertyType.Occupation,
					TranslationFile.Dropdown,
				),
				value: data.employment?.occupationType,
				visible: true,
				col: 12,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownOccupationType'),
			},
			{
				required: false,
				controlName: 'onProbation',
				type: 'checkbox',
				label: t('forms:OnProbation'),
				value: false,
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationType',
								rule: 'in',
								value: employedOccupationTypesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'employedSince',
				type: 'datePicker',
				label: t('forms:EmployedSince'),
				minDate: yearsAgo(100),
				maxDate: new Date(), // today
				value: formatToStringLocale(data.employment?.employedSince),
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationType',
								rule: 'in',
								value: employedOccupationTypesList,
							},
						],
					},
				],
			},
			{
				required: false,
				controlName: 'fixedTimeEmployment',
				type: 'checkbox',
				label: t('forms:FixedTimeEmployment'),
				value: false,
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationType',
								rule: 'in',
								value: employedOccupationTypesList,
							},
						],
					},
				],
			},
			{
				required: true,
				controlName: 'employedTo',
				type: 'datePicker',
				minDate: new Date(), // today
				maxDate: yearsInFuture(100),
				label: t('forms:EmployedTo'),
				visible: false,
				col: 12,
				condition: [
					{
						when: 'all',
						action: 'show',
						items: [
							{
								field: 'occupationType',
								rule: 'in',
								value: employedOccupationTypesList,
							},
							{
								field: 'fixedTimeEmployment',
								rule: 'equal',
								value: 'true',
							},
						],
					},
				],
			},
		],
	}
}

export const ApplicationEmploymentFormUK = (data: IApplicationInformation) => {
	const { t } = useTranslation([TranslationFile.Forms])
	return {
		fieldSetName: '',
		formName: 'ApplicationEmploymentForm',
		collapsable: false,
		show: true,
		visible: true,
		market: appConfig.country,
		fullSize: true,
		col: 12,
		controls: [
			{
				controlName: 'occupationType',
				type: 'dropdown',
				label: t('forms:Occupation'),
				options: FormatEnumToList(
					OccupationType,
					PropertyType.Occupation,
					TranslationFile.Dropdown,
				),
				value: data.employment?.occupationType,
				required: true,
				visible: true,
				col: isMobile ? 12 : 8,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownOccupationType'),
			},
			{
				controlName: 'employedSince',
				type: 'datePicker',
				label: t('forms:EmployedSince'),
				minDate: yearsAgo(100),
				maxDate: new Date(), // today
				value: formatToStringLocale(data.employment?.employedSince),
				required: true,
				visible: true,
				col: isMobile ? 12 : 4,
			},
			{
				controlName: 'employerName',
				type: 'text',
				label: t('forms:EmployerName'),
				value: data.employment?.employerName,
				validations: [
					{
						code: 'MustMatchPattern',
						value: '^.{0,100}$', // 0-100 chars
						message: t('ValidationMsgMaxNChars', { n: 100 }),
					},
				],
				required: false,
				visible: true,
				col: 12,
			},
			{
				controlName: 'jobTitle',
				type: 'dropdown',
				label: t('forms:JobTitle'),
				options: FormatEnumToList(
					JobTitle,
					PropertyType.JobTitle,
					TranslationFile.Dropdown,
				),
				value: data.employment?.jobTitle,
				required: false,
				visible: true,
				col: isMobile ? 12 : 6,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownJobTitle'),
			},
			{
				controlName: 'industry',
				type: 'dropdown',
				label: t('forms:Industry'),
				options: FormatEnumToList(
					Industry,
					PropertyType.Industry,
					TranslationFile.Dropdown,
				),
				value: data.employment?.industry,
				required: false,
				visible: true,
				col: isMobile ? 12 : 6,
				wcagAriaLabel: t('forms:WCAG-Label_DropDownJobIndustry'),
			},
			{
				controlName: 'monthlyNetIncome',
				type: 'number',
				label: t('forms:Income'),
				value: data.employment?.monthlyNetIncome,
				validations: [
					{
						code: 'MustBeGreaterOrEqual',
						value: 0,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
					{
						code: 'MustBeLessOrEqual',
						value: 1e8,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
				],
				required: true,
				visible: true,
				col: isMobile ? 12 : 6,
			},
			{
				controlName: 'otherIncome',
				type: 'text',
				label: t('forms:OtherIncome'),
				value: data.employment?.otherIncome,
				validations: [
					{
						code: 'MustBeGreaterOrEqual',
						value: 0,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
					{
						code: 'MustBeLessOrEqual',
						value: 1e8,
						message: t('forms:ValidationMsgValueBetween', {
							min: 0,
							max: 1e8,
						}),
					},
				],
				required: true,
				visible: true,
				col: isMobile ? 12 : 6,
			},
		],
	}
}
